'use strict';
import defaults from 'lodash/defaults';
import {buildActions} from './actions';
import {buildDeleteFactory} from './defaultDeleteFactory';
import {buildItemFactory} from './defaultItemFactory';
import {buildSyncItemFactory} from './defaultSyncItemFactory';
import {ReduxItemEpics} from './epics';
import {buildReducer} from './reducers';
import {BASE_CACHE_OPTIONS, BASE_OPTIONS, validateOptions} from './utils';


/**
 * @param {ReduxObservableItemOptions} o
 * @constructor
 */
export function ReduxItem(o) {
  /** @type {ReduxObservableItemOptions} */
  const options = {};
  defaults(options, o, BASE_OPTIONS);

  if (!options.itemFactory)
    options.itemFactory = buildItemFactory(options);

  if (!options.syncItemFactory)
    options.syncItemFactory = buildSyncItemFactory(options);

  if (!options.deleteFactory)
    options.deleteFactory = buildDeleteFactory(options);

  if (options.cacheOptions) {
    const cacheOptions = {};
    defaults(cacheOptions, options.cacheOptions, BASE_CACHE_OPTIONS);
    options.cacheOptions = cacheOptions;
  }

  validateOptions(options);

  /** @type {ReduxObservableItemOptions} */
  this.options = options;
  /** @type {ReduxObservableItemActions} */
  this.actions = buildActions(options);
  /** @type {ReduxObservableItemReducers} */
  this.reducers = buildReducer(this.actions, options);
  /** @type {ReduxItemEpics} */
  this.epics = new ReduxItemEpics(this.actions, options, this.reducers);
}

/**
 * @typedef {Object} ReduxItem
 * @property {ReduxObservableItemOptions} options
 * @property {ReduxObservableItemActions} actions
 * @property {ReduxObservableItemReducers} reducers
 * @property {ReduxItemEpics} epics
 */

/**
 * @typedef {Object} ReduxObservableItemOptions
 * @property {String} prefix The prefix for the redux action types. Must be unique across the redux store.
 * @property {Object} [initialState={}] Provide additional initial state properties.
 * @property {AjaxFactoryOptions~getToken} getToken
 * @property {Function} getState Retrieve item state from root state.
 * @property {Logger} [logger]
 * @property {Boolean} [crossDomain=false] Whether to use CORs when making ajax requests.
 * @property {ReduxObservableItemOptions~handleProcessingConflict} [handleProcessingConflict]
 * @property {Function} [isAppReady$] Detect server/network is available and app state is ready to attempt to sync offline cached data
 * @property {Function} [isOnline$] Detect server/network is available
 * @property {Function} [isOffline$] Detect server/network is unavailable
 * @property {?ReduxObservableItemCacheOptions} [cacheOptions]
 *
 * // API Paths:
 * @property {String} baseApiUrl This url will be prepended
 *
 * @property {String|ReduxObservableItemOptions~itemPath} itemPath
 * @property {String} [itemParams] Insert params to the item request. Do not include outer ampersands
 * @property {AjaxFactory} [itemFactory]
 * @property {ReduxObservableItemOptions~parseItemResult} [parseItemResult]
 * @property {ReduxObservableItemOptions~parseItem} [parseItem]
 *
 * @property {String|ReduxObservableItemOptions~itemSyncPath} itemSyncPath
 * @property {ReduxObservableItemOptions~syncNotifier} syncNotifier
 * @property {AjaxFactory} [syncItemFactory]
 * @property {ReduxObservableItemOptions~parseItemSyncResult} [parseItemSyncResult]
 * @property {ReduxObservableItemOptions~parseSyncItem} [parseSyncItem]
 * @property {Number} [syncNotifierThrottle=5000]
 *
 * @property {ReduxObservableItemOptions~deleteItemPath} [deleteItemPath]
 * @property {String} [deleteParams] Insert params to the delete request. Do not include outer ampersands
 * @property {AjaxFactory} [deleteFactory]
 *
 * // Optional features:
 * @property {Boolean} [useDelete=false]
 */

/**
 * @typedef {Object} ReduxObservableItemCacheOptions
 * @property {localForage} localForage
 * @property {Function} getKey
 * @property {String} key
 * @property {Number} [throttle=1000]
 * @property {Function} [reviver]
 * @property {Function} [replacer]
 */

/**
 * Emit a sequence to indicate that it should attempt to update the item.
 * The most trivial example would be long polling with Observable.interval.
 * If using WebSockets, you could subscribe to a channel to be notified of updates.
 *
 * @callback ReduxObservableItemOptions~syncNotifier
 * @param {String} id
 * @param {Store} store
 * @param {Object} dependencies Optional dependencies injected into epics
 * @return {Observable<Void>} The values of the stream are ignored.
 */

/**
 * @callback ReduxObservableItemOptions~deleteItemPath
 * @param {String} id
 * @param {Object} [payload]
 * @return {String} deletion url path
 */

/**
 * @callback ReduxObservableItemOptions~syncScheduler
 * @param {String} id
 * @param {Store} store
 * @return {Observable<String>} Output a stream of prompts to request a sync update
 */

/**
 * @callback ReduxObservableItemOptions~parseItemResult
 * @param {AjaxResponse} response
 * @param {ReduxObservableItemOptions~parseItem} [parseItem]
 * @return {ItemResult}
 */

/**
 * @callback ReduxObservableItemOptions~parseItem
 * @param {T} item
 * @return {T}
 */

/**
 * @callback ReduxObservableItemOptions~parseSyncItem
 * @param {T} item
 * @return {T}
 */

/**
 * @typedef {Object} ItemResult
 * @property {T} item
 */

/**
 * @callback ReduxObservableItemOptions~itemPath
 * @param {String} id
 * @return {String}
 */

/**
 * @callback ReduxObservableItemOptions~parseItemSyncResult
 * @param {AjaxResponse} ajaxResponse
 * @return {ItemSyncResult}
 */

/**
 * @typedef {Object} ItemSyncResult
 * @property {Boolean} hasChange
 * @property {T} [item]
 */

/**
 * @callback ReduxObservableItemOptions~itemSyncPath
 * @param {String} id
 * @return {String}
 */



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/item/index.js